import * as React from 'react'
import { throttle } from 'lodash'
import { EditorState, Transaction } from 'prosemirror-state'
import { EditorView, EditorProps } from 'prosemirror-view'
import { Slice } from 'prosemirror-model'
import { sendableSteps, receiveTransaction, getVersion } from 'prosemirror-collab'
import { Step } from 'prosemirror-transform'
import { withCommandLineAccessors, CommandLineAccessorProps } from 'HOCs/withCommandLineAccessors'
import { History } from 'history'
import * as Sentry from '@sentry/browser'
import qs from 'qs'
import { NoConnectionError } from '@anycable/core'

import { EditorFonts } from 'hooks/useEditorFonts/useEditorFonts'
import { EditorProvider } from 'common/Prose/EditorProvider/EditorProvider'
import { FloatingMenuComponent } from 'common/Prose/Plugins/FloatingMenuAnchorPlugin/FloatingMenuAnchorComponent'
import { PortalTitleBarSaveStatus } from './TitleBarSaveStatus'
import store from 'redux/store'

import {
  RealTimeReceivedPayload,
  UNCONFIRMED_STEP_COUNT_THRESHOLD,
  UNCONFIRMED_STEP_RETRY_TIMEOUT_MS,
  THROTTLE_INTERVAL_MS,
  CONFIRM_LEAVE_MESSAGE,
  REAL_TIME_WS_CHANNEL,
  ARTICLE_VIEW_UPDATE_INTERVAL,
  LONG_DISCONNECT_TIME_MS,
  ConnectionStatus,
  StateSyncStatus,
  SyncingStates,
  SyncStatus,
} from './utils'
import { Layer, Permission, RootState } from 'types'
import { WebsocketLogger } from 'utils/performance/websocketLogger'
import { buffer } from 'common/Prose/Plugins/DataLossDebug'
import { reportError } from 'utils/error'
import { checkFeatureFlagUserGroup, USERS } from 'utils/featureFlagUserGroups'
import { getCollabClientId } from '../StateWithPresenceProvider/utils'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { NodeHandleManager } from 'common/Prose/Plugins/NodeHandles/NodeHandleManager'
import { CollabState, CollabStateContext } from 'stores/CollabState'
import { StatusToast } from './StatusToast'
import { StatusOverlay } from './StatusOverlay'
import { SafariBraveWSModal } from './SafariBraveWSModal/SafariBraveWSModal'
import { ContextualBlockActionsManager } from 'common/Prose/Plugins/ContextualBlockActions/ContextualBlockActionsManager'
import { WebsocketConsumer } from 'providers/WebsocketProvider'
import { isDocumentHidden } from 'utils'
import { DocRightPanelWrapper } from 'components/DocRightPanelWrapper/DocRightPanelWrapper'

interface Props extends CommandLineAccessorProps {
  articleId: string
  crdt: boolean
  isAlmanacUser: boolean
  auth: RootState['auth']
  clientUuid: string
  history: History
  inCommentMode?: boolean
  onTransaction: (tx: Transaction, newState: EditorState, layerId: string) => void
  readOnly?: boolean
  websocketConnection?: WebsocketConsumer
  shouldForceRefreshOnInterval?: boolean
  children?: React.ReactNode
  editorProps?: EditorProps
  updateLayerStates: (payload: RealTimeReceivedPayload) => any
  currentLayer: Layer
  layers: Layer[]
  getLayerState: (layerId: string) => EditorState | null
  isBlaze: boolean
  isMobile: boolean
}

export class RealtimeProvider extends React.Component<Props, { realTimeError: boolean }> {
  articleViewUpdateIntervalId?: ReturnType<typeof setInterval>
  disconnectedAt?: number = Date.now()
  isSyncedUpToRealTime = false
  realTimeReceivedQueue: RealTimeReceivedPayload[] = []
  realTimeSubscription?: {
    perform: (action: string, payload?: any) => void
    unsubscribe: () => void
  }
  sentryTransaction?: any
  sentrySpan?: any
  editorRef = React.createRef<EditorView>()
  providerRef = React.createRef<EditorView>()
  freeze = false
  subscribeRetryTimeout?: ReturnType<typeof setTimeout>
  longDisconnectCheckId: NodeJS.Timeout | null = null
  collabState!: CollabState
  unbindKeepalive?: () => void

  constructor(props: Props) {
    super(props)
    this.collabState = new CollabState()
    this.state = { realTimeError: false }
  }

  applyRealTimePayload = (payload: RealTimeReceivedPayload) => {
    const { articleId, currentLayer } = this.props
    const { layer_id: payloadLayerId, client_uuids, steps: stepsJson, versions } = payload

    const state = this.props.getLayerState(payloadLayerId)
    if (!state || this.editorRef.current == null) return

    // Filter out old steps before the initially loaded version
    const filteredStepsJson = stepsJson.filter((_, i) => versions[i] >= getVersion(state))

    if (!filteredStepsJson.length) return

    const steps = filteredStepsJson.map((s) => Step.fromJSON(state.schema, s))

    try {
      console.debug(`[conn] State ${getVersion(state)} [=] Payload ${Math.min(...payload.versions)}`) // eslint-disable-line no-console
      if (getVersion(state) !== Math.min(...payload.versions)) {
        console.debug(`[conn] version gap ${getVersion(state)} != ${Math.min(...payload.versions)}`) // eslint-disable-line no-console
        this.syncRealtime()
        return
      }

      if (payloadLayerId !== currentLayer.id) {
        return this.props.updateLayerStates(payload)
      }

      const clientUuid = getCollabClientId(state)
      const source = client_uuids[0] !== clientUuid || !this.isSyncedUpToRealTime ? 'remote' : 'local'
      const tr = receiveTransaction(this.editorRef.current.state, steps, client_uuids, { mapSelectionBackward: true })
      for (const metaKey of Object.keys(payload.meta)) {
        tr.setMeta(metaKey, payload.meta[metaKey])
      }
      tr.setMeta('realTime', true)
      tr.setMeta('source', source)
      this.editorRef.current.dispatch(tr)

      this.checkUnconfirmedThreshold()
    } catch (e) {
      const featureFlag = checkFeatureFlagUserGroup([USERS.withAlmanacEmail], store.getState().auth?.email)
      this.setState({ realTimeError: true })
      console.error(e) // eslint-disable-line no-console
      reportError(new Error('ProseEditor encountered a real time issue'), {
        severity: 'error',
        metaData: {
          articleId,
          layer_id: currentLayer.id,
          issue: 'RealtimePayloadException',
          error: e,
          buffer: featureFlag ? buffer?.peekN(buffer?.size()) : 'non feature-flag user',
          docState: featureFlag ? this.editorRef.current?.state?.doc?.toJSON?.() : 'non feature-flag user',
          breakingSteps: featureFlag ? steps : 'non feature-flag user',
        },
      })
      featureFlag && console.warn('buffer', buffer?.peekN(buffer?.size())) // eslint-disable-line no-console
      showErrorMessageToast(
        'Failed to get document. Please try to refresh the page. If this error persists, please contact support.',
      )
      throw e
    }
  }

  applyFromRealTimeReceivedQueue = () => {
    const payloads = this.realTimeReceivedQueue.splice(0, this.realTimeReceivedQueue.length)

    payloads.forEach((p) => {
      this.applyRealTimePayload(p)
    })
  }

  sendSendableSteps = throttle(
    () => {
      const state = this.props.getLayerState(this.props.currentLayer.id)
      if (!state || !this.realTimeSubscription) return

      const { articleId, currentLayer } = this.props
      const stepsToSend = sendableSteps(state)
      if (!stepsToSend) {
        return
      }

      const payload = {
        article_id: articleId,
        layer_id: currentLayer.id,
        client_uuid: stepsToSend.clientID as string,
        version: stepsToSend.version,
        steps: stepsToSend.steps.map((s) => s.toJSON()),
      }
      this.realTimeSubscription.perform('receive', payload)

      WebsocketLogger.logSentMessage(payload)

      this.checkUnconfirmedThreshold()

      if (this.unconfirmedSteps().length >= UNCONFIRMED_STEP_COUNT_THRESHOLD) {
        setTimeout(() => {
          this.sendSendableSteps()
        }, UNCONFIRMED_STEP_RETRY_TIMEOUT_MS)
      }
    },
    THROTTLE_INTERVAL_MS,
    { leading: true, trailing: true },
  )

  checkUnconfirmedThreshold = () => {
    const unconfirmedCount = this.unconfirmedSteps().length

    console.debug(`[conn] Unconfirmed steps ${unconfirmedCount}`) // eslint-disable-line no-console

    if (unconfirmedCount >= UNCONFIRMED_STEP_COUNT_THRESHOLD) {
      this.collabState.setSaveStatus(StateSyncStatus.Behind)
      this.freeze = true
    } else if (unconfirmedCount > 0) {
      this.collabState.setSaveStatus(StateSyncStatus.Saving)
      this.freeze = false
    } else {
      this.collabState.setSaveStatus(StateSyncStatus.UpToDate)
      this.freeze = false
    }
  }

  checkLongConnecting = () => {
    const longCheckFunc = () => {
      this.longDisconnectCheckId = null
      if ([ConnectionStatus.Connecting, ConnectionStatus.Offline].includes(this.collabState.connStatus)) {
        this.collabState.setConnStatus(ConnectionStatus.LongConnecting)
      }
    }

    if (this.longDisconnectCheckId) clearTimeout(this.longDisconnectCheckId)

    this.longDisconnectCheckId = setTimeout(longCheckFunc, LONG_DISCONNECT_TIME_MS)
  }

  unconfirmedSteps = () => {
    const state = this.props.getLayerState(this.props.currentLayer.id)
    return state ? sendableSteps(state)?.steps || [] : []
  }

  /** Request remote steps since collab version recorded in state
   * Throttle this to protect against a tight desync/resync loop
   */
  syncRealtime = throttle(
    () => {
      if (!SyncingStates.includes(this.collabState.syncStatus)) {
        this.collabState.setSyncStatus(SyncStatus.Syncing)
      }
      const { articleId: article_id, clientUuid } = this.props

      this.props.layers.forEach((layer) => {
        const layerState = this.props.getLayerState(layer.id)

        if (!layerState) return

        const syncPayload = {
          article_id,
          layer_id: layer.id,
          client_uuid: clientUuid,
          version: getVersion(layerState) - 1, // Collab starts at 1, backend starts at 0
        }

        this.realTimeSubscription?.perform('push_new_steps', syncPayload)
      })
    },
    500,
    { leading: true },
  )

  onConnect = () => {
    this.syncRealtime()

    // Finish and send transaction if reconnecting after error
    this.sentryTransaction?.finish()
    this.sentrySpan?.finish()
    this.sentryTransaction = undefined
    this.sentrySpan = undefined

    this.disconnectedAt = undefined
    this.collabState.setConnStatus(ConnectionStatus.Connected)
    this.sendSendableSteps()
  }

  onDisconnect = (suppressSentryTransaction?: boolean) => {
    this.disconnectedAt = Date.now()
    this.realTimeReceivedQueue = []
    if (!suppressSentryTransaction) {
      this.sentryTransaction = Sentry.startTransaction(
        { name: 'editor-disconnected' },
        {
          isAlmanac: this.props.isAlmanacUser,
          isThirdPartyConsumer: localStorage.getItem('anycable_tp_client') === 'true',
          isDocumentHidden: isDocumentHidden(),
        },
      )
      this.sentrySpan = this.sentryTransaction.startChild({
        op: 'Reconnect to websocket',
        description: 'User disconnected while editing',
      })
    }
    if (this.collabState.connStatus !== ConnectionStatus.Offline)
      this.collabState.setConnStatus(ConnectionStatus.Connecting)
    this.checkLongConnecting()
  }

  onOffline = () => {
    this.collabState.setConnStatus(ConnectionStatus.Offline)
    this.onDisconnect(true)
  }

  onOnline = () => {
    this.onConnect()
  }

  onBeforeUnload = (event: BeforeUnloadEvent) => {
    if (this.unconfirmedSteps().length) {
      event.preventDefault()
      event.returnValue = CONFIRM_LEAVE_MESSAGE
      return event.returnValue
    }
  }

  editable = () => {
    const noSocketLocation = qs.parse(this.props.history.location.search, { ignoreQueryPrefix: true }).nosocket
    const readModes = this.props.readOnly || this.props.inCommentMode
    const initialSync = this.collabState.syncStatus === SyncStatus.InitialSync
    const longConnecting = this.collabState.connStatus === ConnectionStatus.LongConnecting
    const isEditable = noSocketLocation || this.props.crdt || (!readModes && !initialSync && !longConnecting)
    return isEditable
  }

  subscribe = () => {
    const { websocketConnection, articleId, clientUuid, history, auth } = this.props
    if (!websocketConnection) {
      // Websocket may still be initializing, try again
      this.subscribeRetryTimeout = setTimeout(() => this.subscribe(), 500)
      return
    } else {
      // Otherwise, clear any pending retry
      this.subscribeRetryTimeout = undefined
    }

    window.addEventListener('offline', this.onOffline)
    window.addEventListener('online', this.onOnline)
    window.addEventListener('beforeunload', this.onBeforeUnload)

    history.block(() => {
      if (this.unconfirmedSteps().length) return CONFIRM_LEAVE_MESSAGE
    })

    this.realTimeSubscription = websocketConnection.subscriptions.create(
      { channel: REAL_TIME_WS_CHANNEL, article_id: articleId },
      {
        connected: () => {
          this.onConnect()
        },
        received: (payload: RealTimeReceivedPayload) => {
          if (this.props.currentLayer.permissions.highest === Permission.Read) {
            if (this.editorRef.current) {
              const tr = this.editorRef.current.state.tr.setMeta('initSyncFinished', true)
              this.editorRef.current.dispatch(tr)
            }
            return
          }
          const { meta } = payload

          // Initial sync
          if (meta.initial_sync) {
            // Ignore if the messages are broadcasted for other clients
            if (meta.client_uuid !== clientUuid) return

            this.applyRealTimePayload(payload)
            this.applyFromRealTimeReceivedQueue()

            if (this.collabState.syncStatus === SyncStatus.InitialSync && this.editorRef.current != null) {
              const tr = this.editorRef.current.state.tr.setMeta('initSyncFinished', true)
              this.editorRef.current.dispatch(tr)
            }

            this.collabState.setSyncStatus(SyncStatus.Synced)

            return
          }

          WebsocketLogger.logReceivedMessage(payload)

          this.realTimeReceivedQueue.push(payload)

          if (!SyncingStates.includes(this.collabState.syncStatus)) {
            this.applyFromRealTimeReceivedQueue()
          }
        },
        disconnected: () => {
          console.debug('[conn] disconnect') // eslint-disable-line no-console
          this.onDisconnect()
        },
        rejected: () => {
          console.debug('[conn] rejected') // eslint-disable-line no-console
          this.onDisconnect()
        },
      },
    )

    if (auth) {
      this.realTimeSubscription?.perform('update_last_viewed_at', { layer_id: this.props.currentLayer.id })
      this.articleViewUpdateIntervalId = setInterval(() => {
        if (!isDocumentHidden()) {
          try {
            this.realTimeSubscription?.perform('update_last_viewed_at', { layer_id: this.props.currentLayer.id })
          } catch (e) {
            const isConnectionError =
              e instanceof NoConnectionError ||
              (e instanceof Error &&
                e.message.match(/Subscription is closed|Subscription not found|Channel is not subscribed/))
            if (isConnectionError && this.props.shouldForceRefreshOnInterval) {
              reportError(new Error('Failed to update_last_viewed_at, reconnecting'), { severity: 'warning' })
              this.clearTimers()
              this.subscribe()
            } else {
              reportError(new Error('Failed to update_last_viewed_at, unhandled'), {
                severity: 'error',
                metaData: {
                  shouldForceRefreshOnInterval: this.props.shouldForceRefreshOnInterval,
                  errorMessage: e instanceof Error && e.message,
                },
              })
            }
          }
        }
      }, ARTICLE_VIEW_UPDATE_INTERVAL)
    }
  }

  dispatchTransaction = (tx: Transaction, updatedState: EditorState, layerId: string) => {
    const { onTransaction } = this.props

    onTransaction(tx, updatedState, layerId)

    this.sendSendableSteps()
  }

  componentDidMount() {
    if (!this.props.crdt) this.subscribe()

    document.addEventListener('visibilitychange', this.onVisiblityChange)
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.websocketConnection !== prevProps.websocketConnection) {
      this.subscribe()
    }
  }

  componentWillUnmount() {
    this.props.removeContext('editorView')
    this.clearTimers()
  }

  clearTimers() {
    if (this.subscribeRetryTimeout) clearTimeout(this.subscribeRetryTimeout)
    if (this.articleViewUpdateIntervalId) clearInterval(this.articleViewUpdateIntervalId)
    if (this.realTimeSubscription) this.realTimeSubscription.unsubscribe()

    window.removeEventListener('offline', this.onOffline)
    window.removeEventListener('online', this.onOnline)
    window.removeEventListener('beforeunload', this.onBeforeUnload)
    document.removeEventListener('visibilitychange', this.onVisiblityChange)
  }

  onViewUpdated = (view: EditorView) => {
    this.props.removeContext('editorView')
    this.props.addContext('editorView', view)
  }

  handlePaste = (view: EditorView, event: ClipboardEvent, slice: Slice) => {
    const { handlePaste } = this.props.editorProps || {}

    if (this.freeze) return true
    else if (typeof handlePaste == 'function') return handlePaste(view, event, slice)
    else return false
  }

  handleTextInput = (view: EditorView, from: number, to: number, text: string) => {
    const { handleTextInput } = this.props.editorProps || {}

    if (this.freeze) return true
    else if (typeof handleTextInput == 'function') return handleTextInput(view, from, to, text)
    else return false
  }

  handleKeyDown = (view: EditorView, event: KeyboardEvent) => {
    const { handleKeyDown } = this.props.editorProps || {}

    if (this.freeze) return true
    else if (typeof handleKeyDown == 'function') return handleKeyDown(view, event)
    else return false
  }

  onVisiblityChange = () => {
    if (!isDocumentHidden()) this.syncRealtime()
  }

  transactionShouldBeApplied = (tr: Transaction) => {
    if (this.props.crdt) return false

    const freeze = isDocumentHidden() || (!tr.getMeta('realTime') && this.freeze)
    console.debug(`[freeze] ${freeze}`) // eslint-disable-line no-console
    return freeze
  }

  render() {
    return (
      <>
        <CollabStateContext.Provider value={this.collabState}>
          <EditorFonts />
          <PortalTitleBarSaveStatus />

          <StatusOverlay />
          <StatusToast />
          <SafariBraveWSModal />
          {!this.state.realTimeError && (
            <EditorProvider
              currentLayer={this.props.currentLayer}
              freeze={this.transactionShouldBeApplied}
              onTransaction={this.dispatchTransaction}
              ref={this.editorRef}
              editorProps={{
                editable: this.editable,
                handleTextInput: this.handleTextInput,
                handlePaste: this.handlePaste,
                handleKeyDown: this.handleKeyDown,
                ...this.props.editorProps,
              }}
              internalRef={this.providerRef}
              onViewUpdated={this.onViewUpdated}
              userId={this.props.auth?.id}
            >
              {!this.props.isMobile && <DocRightPanelWrapper />}
              <FloatingMenuComponent />
              <NodeHandleManager />
              <ContextualBlockActionsManager />
              {this.props.children}
            </EditorProvider>
          )}
        </CollabStateContext.Provider>
      </>
    )
  }
}

export default withCommandLineAccessors(RealtimeProvider)
